import React from 'react';
import { useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  HStack,
  IllusEmpty_2,
  Image,
  Modal,
  TextLink,
  theme,
  useResponsive,
} from '@sg/garnish';

import { TransparentModal } from '../../../../components/TransparentModal';
import { useReorderRightIcon } from '../ReorderConfirmLocation/ReorderConfirmLocation.components';

const illustration = IllusEmpty_2;

export const NotOnMenu = ({
  deliveryAddressName,
  restaurantName,
  isLoadingMenu,
  cancelReorder,
  changeLocation,
  viewMenu,
}: Readonly<{
  deliveryAddressName?: string;
  restaurantName?: string;
  isLoadingMenu: boolean;
  cancelReorder: () => void;
  changeLocation: () => void;
  viewMenu: () => void;
}>) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  const Layout = currentBreakpoint.isXS ? MobileLayout : DesktopLayout;

  return (
    <TransparentModal
      testID="reorder-not-on-menu"
      size="medium"
      onClose={cancelReorder}
      mobileEdges={['bottom']}
    >
      <Layout
        isLoadingMenu={isLoadingMenu}
        cancelReorder={cancelReorder}
        changeLocation={changeLocation}
        viewMenu={viewMenu}
      >
        <Modal.Headline>{formatMessage(messages.reorderConflictsNotOnMenuHeadline)}</Modal.Headline>

        <Modal.Row>
          <BodyText size={3} style={styles.bodyText}>
            {deliveryAddressName
              ? formatMessage(messages.reorderConflictsReorderingTo)
              : formatMessage(messages.reorderConflictsReorderingFrom)}{' '}
            <TextLink style={styles.textUnderline} onPress={changeLocation}>
              {deliveryAddressName ? deliveryAddressName : restaurantName}
            </TextLink>
          </BodyText>
        </Modal.Row>

        <Modal.BodyText>{formatMessage(messages.reorderConflictsNotOnMenuNotice)}</Modal.BodyText>
      </Layout>
    </TransparentModal>
  );
};

const DesktopLayout = ({
  children,
  isLoadingMenu,
  changeLocation,
  viewMenu,
  cancelReorder,
}: Readonly<{
  children?: React.ReactNode;
  isLoadingMenu?: boolean;
  cancelReorder: () => void;
  changeLocation: () => void;
  viewMenu: () => void;
}>) => {
  const { formatMessage } = useIntl();

  return (
    <Layout isLoadingMenu={isLoadingMenu} changeLocation={changeLocation} viewMenu={viewMenu}>
      <View style={styles.desktopImageContainer}>
        <Image style={styles.image} source={illustration} />
      </View>
      <View style={styles.content}>
        <Modal.CloseBtn
          onPress={cancelReorder}
          accessibilityHint=""
          accessibilityLabel={formatMessage(messages.notOnMenuClose)}
          testID="reorder.close-button"
        />
        {children}
      </View>
    </Layout>
  );
};

const MobileLayout = ({
  children,
  isLoadingMenu,
  changeLocation,
  viewMenu,
}: Readonly<{
  children?: React.ReactNode;
  isLoadingMenu?: boolean;
  changeLocation: () => void;
  viewMenu: () => void;
}>) => {
  const rightIcon = useReorderRightIcon();
  const navigation = useNavigation();

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => rightIcon,
      headerShown: true,
    });
  }, [navigation, rightIcon]);

  return (
    <Layout isLoadingMenu={isLoadingMenu} changeLocation={changeLocation} viewMenu={viewMenu}>
      <View style={styles.mobileImageContainer}>
        <Image style={styles.image} source={illustration} />
      </View>
      <View style={styles.content}>{children}</View>
    </Layout>
  );
};

const Layout = ({
  children,
  isLoadingMenu,
  changeLocation,
  viewMenu,
}: Readonly<{
  isLoadingMenu?: boolean;
  children?: React.ReactNode;
  changeLocation: () => void;
  viewMenu: () => void;
}>) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  return (
    <>
      <View style={currentBreakpoint.isXS ? styles.column : styles.row}>{children}</View>
      <Modal.Footer style={styles.footer} withoutTopBorder>
        <HStack itemsPerRow={2} gap={theme.spacing['4']}>
          <Button
            size="large"
            palette="secondary"
            onPress={changeLocation}
            accessibilityLabel={formatMessage(messages.reorderChangeLocation)}
            accessibilityHint={formatMessage(messages.reorderChangeLocation)}
            accessibilityRole="button"
            testID="reorder.change-location"
          >
            {formatMessage(messages.reorderChangeLocation)}
          </Button>
          <Button
            size="large"
            onPress={viewMenu}
            accessibilityLabel={formatMessage(messages.reorderViewMenu)}
            accessibilityHint={formatMessage(messages.reorderViewMenu)}
            accessibilityRole="button"
            testID="reorder.view-menu"
            isLoading={isLoadingMenu}
          >
            {formatMessage(messages.reorderViewMenu)}
          </Button>
        </HStack>
      </Modal.Footer>
    </>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  footer: {
    paddingTop: theme.spacing['6'],
  },
  mobileImageContainer: { alignItems: 'center' },
  desktopImageContainer: {
    paddingTop: theme.spacing['6'],
    paddingLeft: theme.spacing['6'],
  },
  image: {
    height: 212,
    width: 212,
  },
  bodyText: {
    paddingBottom: theme.spacing['4'],
  },
  textUnderline: {
    color: theme.colors.KALE,
    textDecorationLine: 'underline',
    textDecorationColor: theme.colors.KALE,
  },
  row: {
    flexDirection: 'row',
  },
  column: {
    flex: 1,
    flexDirection: 'column',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  reorderConflictsNotOnMenuHeadline: {
    defaultMessage: `Oops, that's not on the menu`,
    description: 'Reorder | Conflicts | Not On Menu | Headline',
  },
  reorderConflictsReorderingTo: {
    defaultMessage: 'Reordering to',
    description: 'Reorder | Conflicts | Reordering To',
  },
  reorderConflictsReorderingFrom: {
    defaultMessage: 'Reordering from',
    description: 'Reorder | Conflicts | Reordering From',
  },
  reorderConflictsNotOnMenuNotice: {
    defaultMessage: `Unfortunately it looks like this order can't be made at your location. Sorry about that.`,
    description: 'Reorder | Conflicts | Not On Menu | Notice',
  },
  notOnMenuClose: {
    defaultMessage: 'Close',
    description: 'Not On Menu | Close',
  },
  reorderChangeLocation: {
    defaultMessage: 'Change location',
    description: 'Reorder | Change Location',
  },
  reorderViewMenu: {
    defaultMessage: 'View menu',
    description: 'Reorder | View Menu',
  },
});
